import React, { memo } from 'react';

import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  TextField,
  InputAdornment,
  FormControl
} from '@mui/material';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { useGetAllDealsForDealListId } from '@hooks/useDealLists';
import { getNonAffiliateLink, parseTitle } from '@utils/index';
import AdminActions from '@components/AdminActions';

const EditDeals = memo(
  ({
    ASINsToRemove,
    dealListId,
    onChange
  }: {
    dealListId: number;
    onChange: (ASINs: string[]) => void;
    ASINsToRemove: string[];
  }) => {
    const [filter, setFilter] = React.useState('');
    const { data: deals, isLoading } = useGetAllDealsForDealListId(dealListId);

    if (isLoading) {
      return <Box>Loading...</Box>;
    }

    const renderFilter = () => {
      return (
        <Box component="form" noValidate autoComplete="off" marginTop="16px">
          <FormControl fullWidth size="small">
            <TextField
              id="outlined-basic"
              label="Filter"
              variant="outlined"
              fullWidth
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              InputProps={{
                endAdornment:
                  filter !== '' ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setFilter('')}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null
              }}
            />
          </FormControl>
        </Box>
      );
    };

    const filteredDeals = deals.filter((deal) => {
      return (
        (deal.title || '').toLowerCase().includes(filter.toLowerCase()) ||
        (deal.brand || '').toLowerCase().includes(filter.toLowerCase())
      );
    });

    return (
      <Box>
        <Typography
          gutterBottom
          variant="overline"
        >{`${deals.length} deals in this list`}</Typography>
        {renderFilter()}
        <List>
          {(filteredDeals || []).map((deal) => {
            const isMarkedForDeletion = ASINsToRemove.includes(deal?.ASIN);

            const renderSecondaryActions = () => {
              return (
                <>
                  {isMarkedForDeletion ? (
                    <IconButton
                      edge="end"
                      aria-label="cancel"
                      onClick={() => {
                        onChange(
                          ASINsToRemove.filter((ASIN) => ASIN !== deal?.ASIN)
                        );
                      }}
                      sx={{
                        marginRight: '8px'
                      }}
                    >
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => {
                        onChange([...ASINsToRemove, deal?.ASIN]);
                      }}
                      sx={{
                        marginRight: '8px'
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                  <AdminActions ASIN={deal?.ASIN} deal={deal} />
                </>
              );
            };

            return (
              <ListItem
                key={deal?.ASIN}
                secondaryAction={renderSecondaryActions()}
                sx={{
                  paddingRight: '100px !important'
                }}
              >
                <ListItemAvatar>
                  <IconButton
                    aria-label="see deal"
                    onClick={() => {
                      window.open(getNonAffiliateLink(deal.ASIN), '_blank');
                    }}
                    edge="start"
                  >
                    <Avatar
                      alt={parseTitle(deal.title)}
                      src={
                        deal.image240Url?.replace('.jpg', '.webp') ||
                        deal.image ||
                        deal.image600Url ||
                        ''
                      }
                      variant="square"
                      // largest size
                      sx={{ width: 50, height: 50 }}
                    />
                  </IconButton>
                </ListItemAvatar>
                <ListItemText primary={parseTitle(deal?.title)} />
              </ListItem>
            );
          })}
        </List>
      </Box>
    );
  }
);

export default EditDeals;
